import { DottedBorderSide } from '@components/ui/DottedBorder';
import { LinkButton } from '@components/ui/LinkButton';
import Image from 'next/image';
import glowAvif from './enterprise-glow.avif';
import glowPng from './enterprise-glow.png';
import { InfoTip } from '@components/pricing/InfoTip';

function UptimeIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="inline-block h-4 w-4"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.25"
        d="M8 1.75v.5M11.125 2.587l-.25.433M13.412 4.876l-.433.25M14.25 8h-.5M13.412 11.125l-.433-.25M11.125 13.413l-.25-.433M8 13.75v.5M5.125 12.98l-.25.432M3.02 10.875l-.433.25M2.25 8h-.5M3.02 5.125l-.433-.25M5.125 3.02l-.25-.433"
      />
    </svg>
  );
}

function SupportIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="inline-block h-4 w-4"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.25"
        d="M8 13.25c3.452 0 6.25-2.574 6.25-5.75S11.452 1.75 8 1.75 1.75 4.324 1.75 7.5c0 1.108.34 2.143.931 3.021.225.334.287.762.091 1.113A16.91 16.91 0 0 1 1.75 13.25c.873 0 2.06-.119 3.223-.442.233-.065.48-.05.707.033.718.264 1.5.409 2.32.409Z"
      />
    </svg>
  );
}

function HIPAAIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="inline-block h-4 w-4"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.25"
        d="M2.75 6.75h10.5v5.5a2 2 0 0 1-2 2h-6.5a2 2 0 0 1-2-2v-5.5ZM4.75 6.75V5A3.25 3.25 0 0 1 8 1.75v0A3.25 3.25 0 0 1 11.25 5v1.75"
      />
    </svg>
  );
}

function SlackIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="relative top-px inline-block h-4 w-4"
    >
      <path
        fill="currentColor"
        d="M3.952 9.85a1.47 1.47 0 1 1-2.94 0 1.47 1.47 0 0 1 1.47-1.471h1.47V9.85Zm.735 0a1.47 1.47 0 1 1 2.94 0v3.679a1.47 1.47 0 1 1-2.94 0V9.85ZM9.843 12.057c.813 0 1.47.658 1.47 1.472a1.47 1.47 0 1 1-2.94 0v-1.472h1.47Zm0-.735a1.47 1.47 0 0 1-1.47-1.472 1.47 1.47 0 0 1 1.47-1.471h3.687c.813 0 1.47.658 1.47 1.471a1.47 1.47 0 0 1-1.47 1.472H9.843ZM12.049 6.16a1.47 1.47 0 1 1 2.94 0 1.47 1.47 0 0 1-1.47 1.472h-1.47V6.161Zm-.736 0a1.47 1.47 0 1 1-2.94 0V2.472a1.47 1.47 0 1 1 2.94 0v3.69ZM6.157 3.943a1.47 1.47 0 0 1-1.47-1.472 1.47 1.47 0 1 1 2.94 0v1.472h-1.47Zm0 .746c.813 0 1.47.658 1.47 1.472a1.47 1.47 0 0 1-1.47 1.471H2.47A1.47 1.47 0 0 1 1 6.161a1.47 1.47 0 0 1 1.47-1.472h3.687Z"
      />
    </svg>
  );
}

function OnboardingIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="1.75"
        y="5.75"
        width="8.5"
        height="8.5"
        rx="1"
        stroke="#131316"
        stroke-width="1.25"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M5.75 3.25V2.75C5.75 2.19772 6.19772 1.75 6.75 1.75H13.25C13.8023 1.75 14.25 2.19772 14.25 2.75V9.25C14.25 9.80228 13.8023 10.25 13.25 10.25H12.75"
        stroke="#131316"
        stroke-width="1.25"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

export function PricingEnterprise() {
  return (
    <div className="relative mx-auto bg-newGray-50 sm:px-6 lg:max-w-[76rem] xl:px-0">
      <picture>
        <source srcSet={glowAvif.src} type="image/avif" />
        <Image
          src={glowPng}
          alt=""
          unoptimized
          className="absolute left-1/2 top-[-21.8rem] ml-[-43.8rem] max-w-none mix-blend-overlay"
        />
      </picture>
      <div className="relative px-6 py-12 sm:border-x sm:border-newGray-950/10">
        <DottedBorderSide overhang={0} side="top" />
        <div className="mx-auto max-w-xl text-center">
          <h2 className="text-[0.8125rem]/6 font-semibold text-purple-500">
            Enterprise
          </h2>
          <p className="mt-2 text-xl/7 font-semibold tracking-[-0.015em] text-primary [text-wrap:balance]">
            Need more support and compliance features or pricing doesn't work
            for your business?
          </p>
          <div className="mt-6">
            <LinkButton href="mailto:sales@clerk.dev">Contact sales</LinkButton>
          </div>
        </div>
      </div>
      <div className="relative px-6 py-8 text-secondary sm:border-x sm:border-newGray-950/10">
        <DottedBorderSide overhang={0} side="top" />
        <DottedBorderSide overhang={0} side="bottom" />
        <div className="grid flex-auto flex-wrap items-center justify-center gap-10 sm:flex xl:flex-nowrap">
          <div>
            <UptimeIcon />
            <span className="ml-2 text-[0.8125rem]/6">
              <strong className="font-medium text-primary">
                99.99% Uptime SLA
              </strong>{' '}
            </span>
          </div>

          <p>
            <SupportIcon />
            <span className="ml-2 text-[0.8125rem]/6">
              <strong className="font-medium text-primary">Support SLA</strong>{' '}
            </span>
          </p>

          <p>
            <HIPAAIcon />
            <span className="ml-2 text-[0.8125rem]/6">
              <strong className="font-medium text-primary">
                HIPAA/BAA compliance
              </strong>{' '}
            </span>
          </p>

          <div className="relative flex gap-2">
            <OnboardingIcon />
            <span className="text-[0.8125rem] font-medium text-primary">
              Onboarding & migration support
            </span>
            <span className="relative top-0.5">
              <InfoTip label="Training sessions, architecture review, migration support with a dedicated Customer Success Manager." />
            </span>
          </div>

          <div className="flex gap-2">
            <SlackIcon />
            <span className="text-[0.8125rem] font-medium text-primary">
              Dedicated Slack support
            </span>
            <span className="relative top-0.5">
              <InfoTip label="Direct access to a dedicated Customer Success Manager." />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
